import type { MetricFormat } from '@nono/contract';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { Fragment, type ReactNode, useMemo, useState } from 'react';
import { AdBookings } from '@/components/ads/AdBookings';
import { AdEngagementPanel } from '@/components/ads/AdEngagementPanel';
import { MetricInfoTrigger } from '@/components/data/MetricInfo';
import { CaveatRef } from '@/components/methodology/CaveatRef';
import { Badge } from '@/components/ui/badge';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { useFilters } from '@/lib/filters';
import { formatIsoDate, formatMetric } from '@/lib/format';
import { useLocale, useViewMessages } from '@/lib/i18n';
import { useAds } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { VERDICT_META } from '@/lib/verdict';

/*
 * Ads breakdown — the per-ad / per-campaign leaderboard. A single sortable
 * table; spend & efficiency one row per entity, default-sorted by Bookings
 * desc. Every column here is Meta-side (ad_insights_daily), so the segment
 * toggle changes only which ROWS appear (lead-funnel vs booking-funnel ads),
 * never the column set. No blended P&L lives here — that's the All / Funnel view.
 */

type AdsData = NonNullable<ReturnType<typeof useAds>['data']>;
type AdRow = AdsData['ads'][number];
type Level = 'ad' | 'campaign';

const MESSAGES = {
  nb: {
    title: 'Annonseoversikt',
    subByLevel: (level: string, from: string, to: string) =>
      `Forbruk og effektivitet per ${level}, ${from} → ${to}`,
    levelAd: 'annonse',
    levelCampaign: 'kampanje',
    levelAdLabel: 'Annonse',
    levelCampaignLabel: 'Kampanje',
    colName: 'Annonse / kampanje',
    colBookings: 'Bookinger',
    colSpend: 'Forbruk',
    colImpressions: 'Visn.',
    colClicks: 'Klikk',
    colCtr: 'CTR',
    colCpc: 'CPC',
    colLeads: 'Leads',
    colCpl: 'CPL',
    colCostPerBooking: 'Kostnad/booking',
    colRevenue: 'Inntekt',
    colRoi: 'ROI',
    scheduleNote: (count: string) =>
      `${count} nettside-bookinger tilskrevet av Meta i perioden — bookinger kommer rett fra Metas booking-konvertering, så de matcher Ads Manager.`,
    revenueNote: (att: string, total: string, sess: string) =>
      `${att} av ${total} medlemsinntekt i perioden er knyttet til en annonse (${sess} sesjonsbekreftet). Inntekt per annonse tilskrives via booking→annonse-matchen, netto etter refusjon, på første-måned/periode-blandet basis — Meta Ads Manager er fortsatt fasit.`,
    emptyRange: 'Ingen annonsedata for denne perioden ennå.',
    emptyHint: 'Velg en annen periode, eller kjør en synk.',
    sortAsc: 'stigende',
    sortDesc: 'synkende',
    engagementToggle: 'Vis engasjement-diagnostikk',
  },
  en: {
    title: 'Ads breakdown',
    subByLevel: (level: string, from: string, to: string) =>
      `Spend & efficiency per ${level}, ${from} → ${to}`,
    levelAd: 'ad',
    levelCampaign: 'campaign',
    levelAdLabel: 'Ad',
    levelCampaignLabel: 'Campaign',
    colName: 'Ad / campaign',
    colBookings: 'Bookings',
    colSpend: 'Spend',
    colImpressions: 'Impr',
    colClicks: 'Clicks',
    colCtr: 'CTR',
    colCpc: 'CPC',
    colLeads: 'Leads',
    colCpl: 'CPL',
    colCostPerBooking: 'Cost/Booking',
    colRevenue: 'Revenue',
    colRoi: 'ROI',
    scheduleNote: (count: string) =>
      `${count} website bookings attributed by Meta in range — bookings come straight from Meta's website-booking conversion, so they match Ads Manager.`,
    revenueNote: (att: string, total: string, sess: string) =>
      `${att} of ${total} membership revenue in range is tied to an ad (${sess} session-proven). Per-ad revenue is attributed via the booking→ad match, net of refunds, on a first-month / period-blended basis — Meta Ads Manager stays the source of truth.`,
    emptyRange: 'No ad-level data yet for this range.',
    emptyHint: 'Pick another range, or run a sync.',
    sortAsc: 'ascending',
    sortDesc: 'descending',
    engagementToggle: 'Show engagement diagnostics',
  },
};

type Msgs = (typeof MESSAGES)['nb'];

// Sortable/displayable columns are the scalar fields only — the nested
// `engagement` + `recommendation` objects live in the expandable row, never a column.
type ColKey = keyof Omit<AdRow, 'engagement' | 'recommendation'>;

interface Column {
  key: ColKey;
  label: string;
  format: MetricFormat;
  /** Decision metric — reserves the status-tick slot (no target wired today). */
  decision?: boolean;
}

export function AdsPage() {
  const m = useViewMessages(MESSAGES);
  const { from, to } = useFilters();
  const [level, setLevel] = useState<Level>('ad');

  const adsQuery = useAds(level);

  if (adsQuery.isLoading)
    return (
      <PageFrame m={m} from={from} to={to} level={level} onLevel={setLevel}>
        <LoadingState />
      </PageFrame>
    );
  if (adsQuery.isError || !adsQuery.data)
    return (
      <PageFrame m={m} from={from} to={to} level={level} onLevel={setLevel}>
        <ErrorState onRetry={() => adsQuery.refetch()} />
      </PageFrame>
    );

  const data = adsQuery.data;

  return (
    <PageFrame m={m} from={from} to={to} level={data.level} onLevel={setLevel}>
      {data.ads.length === 0 ? (
        <EmptyState title={m.emptyRange}>{m.emptyHint}</EmptyState>
      ) : (
        <>
          <AdsTable m={m} rows={data.ads} level={data.level} />
          <p className="flex items-start gap-2 text-label text-fg-muted">
            <CaveatRef id="bookingIsMeta" />
            <span>{m.scheduleNote(formatMetric(data.scheduleTotal, 'int'))}</span>
          </p>
          {data.revenueTotal > 0 ? (
            <p className="text-label text-fg-muted">
              {m.revenueNote(
                formatMetric(data.revenueAttributed, 'nok'),
                formatMetric(data.revenueTotal, 'nok'),
                formatMetric(data.sessionAttributed, 'nok'),
              )}
            </p>
          ) : null}
        </>
      )}
    </PageFrame>
  );
}

function PageFrame({
  m,
  from,
  to,
  level,
  onLevel,
  children,
}: {
  m: Msgs;
  from: string;
  to: string;
  level: Level;
  onLevel: (l: Level) => void;
  children: ReactNode;
}) {
  const levelLabel = level === 'campaign' ? m.levelCampaign : m.levelAd;
  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 text-title text-fg">
            {m.title}
            <CaveatRef id="segmentHardStop" />
          </h1>
          <p className="mt-1 text-body text-fg-muted">
            {m.subByLevel(levelLabel, formatIsoDate(from).date, formatIsoDate(to).date)}
          </p>
        </div>
        <LevelToggle m={m} value={level} onChange={onLevel} />
      </header>
      {children}
    </div>
  );
}

function LevelToggle({
  m,
  value,
  onChange,
}: {
  m: Msgs;
  value: Level;
  onChange: (l: Level) => void;
}) {
  const items: { value: Level; label: string }[] = [
    { value: 'ad', label: m.levelAdLabel },
    { value: 'campaign', label: m.levelCampaignLabel },
  ];
  return (
    <div className="flex h-10 items-center rounded-md border border-border bg-surface p-0.5 sm:h-8">
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          onClick={() => onChange(item.value)}
          aria-pressed={item.value === value}
          className={cn(
            'flex h-9 items-center rounded px-3 text-label transition-fast sm:h-7 sm:px-2.5',
            item.value === value
              ? 'bg-surface-3 font-medium text-fg'
              : 'text-fg-subtle hover:text-fg',
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

type SortDir = 'asc' | 'desc';

function AdsTable({ m, rows, level }: { m: Msgs; rows: AdRow[]; level: Level }) {
  const columns: Column[] = useMemo(
    () => [
      { key: 'name', label: m.colName, format: 'text' },
      { key: 'bookings', label: m.colBookings, format: 'int', decision: true },
      { key: 'spend', label: m.colSpend, format: 'nok' },
      { key: 'impressions', label: m.colImpressions, format: 'int' },
      { key: 'clicks', label: m.colClicks, format: 'int' },
      { key: 'ctr', label: m.colCtr, format: 'pct' },
      { key: 'cpc', label: m.colCpc, format: 'nok' },
      { key: 'leads', label: m.colLeads, format: 'int' },
      { key: 'cpl', label: m.colCpl, format: 'nok', decision: true },
      { key: 'cpBooking', label: m.colCostPerBooking, format: 'nok', decision: true },
      { key: 'revenue', label: m.colRevenue, format: 'nok', decision: true },
      { key: 'rRoi', label: m.colRoi, format: 'ratio', decision: true },
    ],
    [m],
  );

  const [sortKey, setSortKey] = useState<ColKey>('bookings');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const sorted = useMemo(() => {
    const isString = (k: ColKey) => k === 'name';
    const next = rows.slice();
    next.sort((a, b) => {
      if (isString(sortKey)) {
        const va = String(a[sortKey] ?? '').toLowerCase();
        const vb = String(b[sortKey] ?? '').toLowerCase();
        if (va === vb) return 0;
        const cmp = va < vb ? -1 : 1;
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const va = a[sortKey];
      const vb = b[sortKey];
      const na = va == null || (typeof va === 'number' && Number.isNaN(va));
      const nb = vb == null || (typeof vb === 'number' && Number.isNaN(vb));
      // Nulls always sort last, regardless of direction.
      if (na && nb) return 0;
      if (na) return 1;
      if (nb) return -1;
      const diff = Number(va) - Number(vb);
      return sortDir === 'asc' ? diff : -diff;
    });
    return next;
  }, [rows, sortKey, sortDir]);

  function onSort(key: ColKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  }

  return (
    <div className="-mx-4 overflow-x-auto rounded-none border-x-0 border-y border-border bg-surface shadow-card sm:mx-0 sm:rounded-lg sm:border">
      <table className="w-full min-w-[56rem] border-collapse text-body">
        <thead>
          <tr className="border-b border-border bg-surface-2">
            {columns.map((c, i) => {
              const active = c.key === sortKey;
              const arrowLabel = sortDir === 'asc' ? m.sortAsc : m.sortDesc;
              return (
                <th
                  key={c.key}
                  scope="col"
                  aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  className={cn(
                    'px-3 py-2.5 text-label font-medium text-fg-muted sm:py-2',
                    i === 0
                      ? 'sticky left-0 z-10 border-r border-border bg-surface-2 text-left'
                      : 'whitespace-nowrap text-right',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onSort(c.key)}
                    title={`${arrowLabel}`}
                    className={cn(
                      'inline-flex min-h-9 items-center gap-1 transition-fast hover:text-fg sm:min-h-0',
                      i === 0 ? 'flex-row' : 'flex-row-reverse',
                      active && 'text-fg',
                    )}
                  >
                    <span className="inline-flex items-center gap-1">
                      <MetricInfoTrigger metricKey={c.key} label={c.label} />
                      {c.key === 'bookings' ? <CaveatRef id="bookingIsMeta" /> : null}
                    </span>
                    <span aria-hidden className="flex w-3.5 items-center text-fg-subtle">
                      {active ? (
                        sortDir === 'asc' ? (
                          <ChevronUp className="size-3.5" />
                        ) : (
                          <ChevronDown className="size-3.5" />
                        )
                      ) : null}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const isOpen = expanded.has(row.entityId);
            return (
              <Fragment key={row.entityId}>
                <tr className="group border-b border-border/60 transition-fast hover:bg-surface-2/50">
                  {columns.map((c, i) =>
                    i === 0 ? (
                      <th
                        key={c.key}
                        scope="row"
                        className="sticky left-0 z-10 max-w-[18rem] border-r border-border bg-surface px-3 py-2 text-left font-normal text-fg transition-fast group-hover:bg-surface-2"
                      >
                        <button
                          type="button"
                          onClick={() => toggleExpand(row.entityId)}
                          aria-expanded={isOpen}
                          title={m.engagementToggle}
                          className="flex max-w-full items-center gap-1.5 text-left transition-fast hover:text-fg"
                        >
                          {isOpen ? (
                            <ChevronDown className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />
                          ) : (
                            <ChevronRight
                              className="size-3.5 shrink-0 text-fg-subtle"
                              aria-hidden
                            />
                          )}
                          <span className="truncate" title={String(row.name || '')}>
                            {row.name || '—'}
                          </span>
                          <VerdictChip recommendation={row.recommendation} />
                        </button>
                      </th>
                    ) : (
                      <td key={c.key} className="px-3 py-2 text-right">
                        <DataCell
                          value={asNum(row[c.key])}
                          format={c.format}
                          emphasize={c.key === 'bookings'}
                        />
                      </td>
                    ),
                  )}
                </tr>
                {isOpen ? (
                  <tr className="border-b border-border/60">
                    <td colSpan={columns.length} className="bg-surface-2 px-3 py-3">
                      <div className="sticky left-0 w-[calc(100vw-3rem)] max-w-full md:w-auto">
                        <AdEngagementPanel
                          engagement={row.engagement}
                          recommendation={row.recommendation}
                        />
                        {level === 'ad' ? (
                          <AdBookings adId={row.entityId} metaBookings={row.bookings} />
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Compact verdict chip next to the ad name (full reasoning in the dropdown). */
function VerdictChip({ recommendation }: { recommendation: AdRow['recommendation'] }) {
  const { locale } = useLocale();
  const meta = VERDICT_META[recommendation.verdict];
  return (
    <Badge tone={meta.tone} className="shrink-0">
      {meta[locale]}
    </Badge>
  );
}

function DataCell({
  value,
  format,
  emphasize,
}: {
  value: number | null;
  format: MetricFormat;
  emphasize: boolean;
}) {
  const isPositive = emphasize && value != null && value > 0;
  return (
    <span
      className={cn(
        'inline-block whitespace-nowrap tabular',
        isPositive ? 'font-medium text-fg' : 'text-fg',
      )}
    >
      {formatMetric(value, format)}
    </span>
  );
}

function asNum(v: number | string | null | undefined): number | null {
  return typeof v === 'number' ? v : null;
}
